'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { mapBill, mapRecurring, mapReminder, mergeHouseholdItems } from '@/lib/piai/mapSources';
import {
  createManualItem,
  loadManualItems,
  removeManualItem,
  type ManualItemInput,
} from '@/lib/piai/manualItems';
import { expandMockAcrossYear } from '@/lib/piai/mockHousehold';
import { summarizeMonth } from '@/lib/piai/status';
import type { HouseholdItem, HouseholdMonthState, HouseholdStatus } from '@/lib/piai/types';

export const DISCOVERY_KEY = 'piai-discovery-accepted';
export const CONNECTIONS_KEY = 'piai-connections';
export const PIAI_CONNECTIONS_EVENT = 'piai-connections-changed';

const OVERRIDES_KEY = 'piai-item-overrides';

type Overrides = Record<string, Partial<Pick<HouseholdItem, 'status'>>>;

function readOverrides(): Overrides {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(OVERRIDES_KEY) || '{}') as Overrides;
  } catch {
    return {};
  }
}

function writeOverrides(next: Overrides) {
  localStorage.setItem(OVERRIDES_KEY, JSON.stringify(next));
}

function readBankConnected(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = JSON.parse(localStorage.getItem(CONNECTIONS_KEY) || '{}') as {
      bank?: boolean;
    };
    return Boolean(raw.bank);
  } catch {
    return false;
  }
}

function isDiscoveryCandidate(item: HouseholdItem): boolean {
  return item.source === 'bank_detected' || item.source === 'recurring';
}

function inMonth(item: HouseholdItem, reference: Date): boolean {
  const d = new Date(item.dueAt);
  return d.getFullYear() === reference.getFullYear() && d.getMonth() === reference.getMonth();
}

async function fetchJson(url: string, init?: RequestInit): Promise<Record<string, unknown> | null> {
  try {
    const res = await fetch(url, {
      credentials: 'include',
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers || {}),
      },
    });
    if (!res.ok) return null;
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function reminderApiId(item: HouseholdItem): string | null {
  if (item.rawSourceId) return item.rawSourceId;
  if (item.id.startsWith('reminder-')) return item.id.slice('reminder-'.length);
  return null;
}

/** Call after a bank connect so the calendar can run the discovery moment. */
export function markDiscoveryPending() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(DISCOVERY_KEY);
  window.dispatchEvent(new Event(PIAI_CONNECTIONS_EVENT));
}

export function useHouseholdMonth(reference = new Date()) {
  const monthKey = `${reference.getFullYear()}-${reference.getMonth()}`;
  const [items, setItems] = useState<HouseholdItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromApi, setFromApi] = useState(false);
  const [discoveryAccepted, setDiscoveryAccepted] = useState(false);
  const [bankConnected, setBankConnected] = useState(false);
  const [settlingIds, setSettlingIds] = useState<string[]>([]);

  const applyOverrides = useCallback((list: HouseholdItem[]) => {
    const overrides = readOverrides();
    return list.map((item) =>
      overrides[item.id] ? { ...item, ...overrides[item.id] } : item
    );
  }, []);

  const syncConnectionFlags = useCallback(() => {
    setBankConnected(readBankConnected());
    setDiscoveryAccepted(localStorage.getItem(DISCOVERY_KEY) === '1');
  }, []);

  useEffect(() => {
    syncConnectionFlags();
    window.addEventListener(PIAI_CONNECTIONS_EVENT, syncConnectionFlags);
    window.addEventListener('storage', syncConnectionFlags);
    return () => {
      window.removeEventListener(PIAI_CONNECTIONS_EVENT, syncConnectionFlags);
      window.removeEventListener('storage', syncConnectionFlags);
    };
  }, [syncConnectionFlags]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    syncConnectionFlags();

    const [y, m] = monthKey.split('-').map(Number);
    const ref = new Date(y, m, 15);

    const [piaiRes, billsRes, recurringRes] = await Promise.all([
      fetchJson('/api/piai/items'),
      fetchJson('/api/finance/bills'),
      fetchJson('/api/finance/recurring-transactions'),
    ]);

    const piaiReminders = Array.isArray(piaiRes?.billReminders)
      ? (piaiRes!.billReminders as Record<string, unknown>[]).map(mapReminder)
      : Array.isArray(piaiRes?.items)
        ? (piaiRes!.items as Record<string, unknown>[]).map(mapReminder)
        : [];

    const bills = Array.isArray(billsRes?.bills)
      ? (billsRes!.bills as Record<string, unknown>[]).map(mapBill)
      : [];
    const recurring = Array.isArray(recurringRes?.recurringTransactions)
      ? (recurringRes!.recurringTransactions as Record<string, unknown>[]).map(mapRecurring)
      : [];

    const live = mergeHouseholdItems([piaiReminders, bills, recurring]);
    const mock = expandMockAcrossYear(ref);
    const localManual = loadManualItems();
    const hasLive = live.length > 0;
    setFromApi(hasLive);

    const eventMocks = mock.filter((i) => i.category === 'event' || i.source === 'mock');
    const base = hasLive ? mergeHouseholdItems([live, eventMocks]) : mock;
    const combined = mergeHouseholdItems([base, localManual]);

    setItems(applyOverrides(combined));
    setLoading(false);
  }, [applyOverrides, monthKey, syncConnectionFlags]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateStatusLocal = useCallback((id: string, status: HouseholdStatus) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, status } : item)));
    const overrides = readOverrides();
    overrides[id] = { status };
    writeOverrides(overrides);
  }, []);

  const markHandled = useCallback(
    async (id: string) => {
      const item = items.find((i) => i.id === id);
      const apiId = item ? reminderApiId(item) : null;

      if (apiId && (item?.source === 'manual' || item?.source === 'reminder')) {
        await fetchJson(`/api/piai/items/${apiId}`, {
          method: 'PATCH',
          body: JSON.stringify({ action: 'handled' }),
        });
      }

      updateStatusLocal(id, 'done');
    },
    [items, updateStatusLocal]
  );

  const remindMe = useCallback(
    async (id: string) => {
      const item = items.find((i) => i.id === id);
      const apiId = item ? reminderApiId(item) : null;

      if (apiId && (item?.source === 'manual' || item?.source === 'reminder')) {
        await fetchJson(`/api/piai/items/${apiId}`, {
          method: 'PATCH',
          body: JSON.stringify({ action: 'remind' }),
        });
      }

      updateStatusLocal(id, 'needs_attention');
    },
    [items, updateStatusLocal]
  );

  const discoveredItems = useMemo(() => {
    return items
      .filter((i) => isDiscoveryCandidate(i) && inMonth(i, reference))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [items, reference]);

  const acceptDiscovery = useCallback(() => {
    const ids = discoveredItems.map((i) => i.id);
    localStorage.setItem(DISCOVERY_KEY, '1');
    setDiscoveryAccepted(true);
    setSettlingIds(ids);
    window.setTimeout(() => setSettlingIds([]), 1400);
  }, [discoveredItems]);

  const addItem = useCallback(async (input: ManualItemInput) => {
    const res = await fetchJson('/api/piai/items', {
      method: 'POST',
      body: JSON.stringify(input),
    });

    if (res?.item || res?.billReminder) {
      const raw = (res.item || res.billReminder) as Record<string, unknown>;
      const created = mapReminder(raw);
      setItems((prev) => mergeHouseholdItems([prev, [created]]));
      setFromApi(true);
      return created;
    }

    const created = createManualItem(input);
    setItems((prev) => mergeHouseholdItems([prev, [created]]));
    return created;
  }, []);

  const deleteItem = useCallback(async (id: string) => {
    const item = items.find((i) => i.id === id);
    const apiId = item ? reminderApiId(item) : null;

    if (apiId && item?.source === 'manual') {
      await fetchJson(`/api/piai/items/${apiId}`, { method: 'DELETE' });
    }
    if (id.startsWith('manual-')) {
      removeManualItem(id);
    }

    setItems((prev) => prev.filter((i) => i.id !== id));
    const overrides = readOverrides();
    delete overrides[id];
    writeOverrides(overrides);
  }, [items]);

  const visibleItems = useMemo(() => {
    return items.filter((i) => {
      if (!isDiscoveryCandidate(i)) return true;
      // Bank-detected obligations only appear after connect + accept
      if (!bankConnected && !discoveryAccepted) return false;
      if (!discoveryAccepted) return false;
      return true;
    });
  }, [items, discoveryAccepted, bankConnected]);

  const showDiscovery = bankConnected && !discoveryAccepted && discoveredItems.length > 0;

  const state: HouseholdMonthState = {
    items: visibleItems,
    summary: summarizeMonth(visibleItems, reference),
    discoveredCount: discoveredItems.length,
    loading,
    error,
    fromApi,
  };

  return {
    ...state,
    allItems: items,
    discoveredItems,
    showDiscovery,
    bankConnected,
    discoveryAccepted,
    settlingIds,
    acceptDiscovery,
    markHandled,
    remindMe,
    updateStatus: updateStatusLocal,
    addItem,
    deleteItem,
    reload: load,
  };
}
